import { useQueryClient } from "@tanstack/react-query";
import { useMerchant } from "../../../../hooks/useMerchant";
import { toaster } from "../../../../components/ui/toaster";
import {
  ReportColumn,
  buildReportSheet,
  downloadExcelBlob,
} from "../../../../helpers/excel";
import { useTRPC } from "../../../../trpc/client";
import { SubscriptionCharge } from "./useFetchRewardRecipients";

const nameColumn: ReportColumn<SubscriptionCharge> = {
  header: "Nombre",
  value: (charge) => charge.customerName ?? "",
  width: 30,
};

const documentColumn: ReportColumn<SubscriptionCharge> = {
  header: "Documento",
  value: (charge) => charge.customerDocumentNumber,
  width: 18,
};

const billingMonthColumn: ReportColumn<SubscriptionCharge> = {
  header: "Mes de facturación",
  value: (charge) => charge.billingMonth,
  width: 18,
};

const amountColumn: ReportColumn<SubscriptionCharge> = {
  header: "Monto suscripción",
  value: (charge) => charge.amount,
  width: 18,
  numeric: true,
};

export function useDownloadSubscriptionReports() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { merchant } = useMerchant();

  const downloadRewardRecipientsReport = (
    charges: SubscriptionCharge[]
  ): void => {
    const blob = buildReportSheet<SubscriptionCharge>({
      title: "Ganadores Premio Palomma Plus",
      columns: [
        nameColumn,
        documentColumn,
        billingMonthColumn,
        amountColumn,
        {
          header: "Premio",
          value: (charge) => charge.rewardAmount ?? 0,
          width: 18,
          numeric: true,
        },
      ],
      rows: charges,
    });
    downloadExcelBlob(blob, "ganadores_premio.xlsx");
  };

  const downloadMonthlyCostReport = async (
    billingMonth: string
  ): Promise<{ downloaded: boolean }> => {
    if (!merchant?.id) return { downloaded: false };

    let charges: SubscriptionCharge[];
    try {
      charges = await queryClient.fetchQuery(
        trpc.subscriptions.listSubscriptionCharges.queryOptions({
          rentalsMerchantId: merchant.id,
          billingMonth,
          status: "paid",
        })
      );
    } catch (error) {
      console.error("Error fetching subscription charges for report:", error);
      toaster.create({
        title: "Error",
        description: "Hubo un error al generar el reporte. Intenta nuevamente.",
        type: "error",
        duration: 5000,
        meta: { closable: true },
      });
      return { downloaded: false };
    }

    if (charges.length === 0) {
      toaster.create({
        title: "Sin cargos",
        description: "No hay cargos pagados para este mes.",
        type: "warning",
        duration: 5000,
        meta: { closable: true },
      });
      return { downloaded: false };
    }

    const totalMerchantCost = charges.reduce(
      (sum, charge) => sum + charge.merchantCost,
      0
    );

    const monthColumn: ReportColumn<SubscriptionCharge> = {
      ...billingMonthColumn,
      header: "Mes",
    };
    const monthlyAmountColumn: ReportColumn<SubscriptionCharge> = {
      ...amountColumn,
      header: "Monto",
    };

    const hasCommission = totalMerchantCost > 0;
    const columns: ReportColumn<SubscriptionCharge>[] = [
      nameColumn,
      documentColumn,
      monthColumn,
      monthlyAmountColumn,
      ...(hasCommission
        ? [
            {
              header: "Comisiones",
              value: (charge: SubscriptionCharge) => charge.merchantCost,
              width: 18,
              numeric: true,
            },
          ]
        : []),
    ];

    const blob = buildReportSheet<SubscriptionCharge>({
      title: `Reporte de Pagos Palomma Plus - ${billingMonth}`,
      columns,
      rows: charges,
      totalRow: hasCommission
        ? {
            label: "Total comisiones",
            valueColumnIndex: columns.length - 1,
            value: totalMerchantCost,
          }
        : undefined,
    });
    downloadExcelBlob(blob, `pagos_palomma_plus_${billingMonth}.xlsx`);
    return { downloaded: true };
  };

  return { downloadRewardRecipientsReport, downloadMonthlyCostReport };
}
